import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash-es';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import {
	SEARCH_CACHE_SECONDS,
	SEARCH_LOCATION_QUERY_KEY,
	SEARCH_LOCATION_CITY_MAX_GROUPS,
	SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS,
	TYPESENSE_LOCATION_COLLECTION,
	TYPESENSE_LOCATION_FILTER,
} from '#src/app/pages/search/shared/consts';

import { useSearchClient } from '../../../../../useSearchClient';
import type { RankedLocationItem } from '../../../../utils';
import { getMatchTier, normalizeLocationText } from '../../../../utils';
import { useCityPetitionCounts } from '../useCityPetitionCounts';

type LocationHit = {
	id: string;
	city: string;
	state_code: string;
	country_code: string;
};

type CityGroup = {
	document: LocationHit;
	rowCount: number;
	tier: number;
	// other city_state values folded into this one, which the filter matches too
	mergedValues?: string[];
	// label with the country instead of the state, e.g. "London, United Kingdom" for "London, England"
	showCountry?: boolean;
};

function getCityOrStateLocation(city?: string, stateCode?: string): string {
	if (!city || !stateCode) return `${city || stateCode}`;
	return `${city}, ${stateCode}`;
}

const placeKey = ({ document: { city, country_code: countryCode } }: CityGroup) => `${city}|${countryCode}`;

/**
 * The city_state values whose petition counts decide a merge: stateless cities with a stateful sibling in the same
 * country, and those siblings. Empty for most queries (e.g. US cities), which then need no counts request
 */
function getMergeCandidates(groups: readonly CityGroup[]): string[] {
	const statelessKeys = new Set(groups.filter(({ document }) => !document.state_code).map(placeKey));
	const siblings = groups.filter(
		(group) => group.document.city && group.document.state_code && statelessKeys.has(placeKey(group)),
	);
	if (!siblings.length) return [];
	const siblingKeys = new Set(siblings.map(placeKey));
	const stateless = groups.filter((group) => !group.document.state_code && siblingKeys.has(placeKey(group)));
	return [...stateless, ...siblings].map(({ document: { city, state_code: stateCode } }) =>
		getCityOrStateLocation(city, stateCode),
	);
}

/**
 * Older location rows can lack a state ("London", GB) while newer petitions are saved with one ("London, England"),
 * so offering both splits a place's petitions. Folds each stateless city into its sibling in the same country with
 * the most petitions, and keeps the stateless value so the filter still matches the older petitions.
 * A sibling with no petitions never absorbs it ("Madrid, Comun"), and without counts nothing is merged.
 * When the sibling is the only one, it keeps the country label, which matches how petition cards show the location
 */
function mergeStatelessCities(
	groups: readonly CityGroup[],
	petitionCounts: Readonly<Record<string, number>> | undefined,
): CityGroup[] {
	if (!petitionCounts) return [...groups];

	const petitionCount = ({ document: { city, state_code: stateCode } }: CityGroup) =>
		petitionCounts[getCityOrStateLocation(city, stateCode)] ?? 0;

	const busiestWithState = new Map<string, CityGroup>();
	const withStateCounts = new Map<string, number>();
	groups.forEach((group) => {
		if (!group.document.city || !group.document.state_code) return;
		const key = placeKey(group);
		withStateCounts.set(key, (withStateCounts.get(key) ?? 0) + 1);
		if (!petitionCount(group)) return;
		const current = busiestWithState.get(key);
		if (!current || petitionCount(group) > petitionCount(current)) busiestWithState.set(key, group);
	});

	// groups are unique by city, state and country, so each sibling absorbs at most one stateless group
	const absorbed = new Map<CityGroup, CityGroup>();
	const kept = groups.filter((group) => {
		if (group.document.state_code) return true;
		const sibling = busiestWithState.get(placeKey(group));
		if (!sibling) return true;
		absorbed.set(sibling, group);
		return false;
	});

	return kept.map((group) => {
		const stateless = absorbed.get(group);
		if (!stateless) return group;
		return {
			...group,
			rowCount: group.rowCount + stateless.rowCount,
			mergedValues: [stateless.document.city],
			// "Springfield, United States" would be ambiguous when there are several
			showCountry: withStateCounts.get(placeKey(group)) === 1,
		};
	});
}

type Result = ModelHookResult<
	undefined,
	{
		searchForCities: (queryLocation: string, selectedLocation: string) => Promise<readonly RankedLocationItem[]>;
	}
>;

export function useSearchForCities(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		actions: { getCityPetitionCounts },
	} = useCityPetitionCounts();

	const queryClient = useQueryClient();
	// counts that land after the timeout are cached by then, so fetching the suggestions again merges them
	const refetchSuggestions = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: [SEARCH_LOCATION_QUERY_KEY] });
	}, [queryClient]);

	const {
		data: { countries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(countries.map((c) => [c.countryCode, c.displayName])),
		[countries],
	);

	return {
		actions: {
			searchForCities: useCallback(
				async (queryLocation: string, selectedLocation: string) => {
					// the collection has one doc per site DB row (e.g. hundreds for "Los Angeles, CA"),
					// so we group by place and use the group size as a popularity signal
					const response = await typesenseClient
						.collections<LocationHit>(TYPESENSE_LOCATION_COLLECTION)
						.documents()
						.search(
							{
								q: queryLocation,
								query_by: TYPESENSE_LOCATION_FILTER,
								group_by: 'city,state_code,country_code',
								group_limit: 1,
								per_page: SEARCH_LOCATION_CITY_MAX_GROUPS,
							},
							{
								cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS,
							},
						);

					if (!Array.isArray(response.grouped_hits) || !response.grouped_hits.length) return [];

					const normalizedQuery = normalizeLocationText(queryLocation);

					const groups = response.grouped_hits
						// group_limit: 1, so each group has a single hit
						.flatMap(({ hits, found }) =>
							hits.map(({ document }) => ({
								document,
								rowCount: found ?? 1,
								tier: getMatchTier(document.city || '', normalizedQuery),
							})),
						);

					// counted by exact value, so typos in the query ("londn") don't matter
					const petitionCounts = await getCityPetitionCounts(getMergeCandidates(groups), refetchSuggestions);

					const ranked = mergeStatelessCities(groups, petitionCounts).sort(
						(a, b) => a.tier - b.tier || b.rowCount - a.rowCount,
					);

					return uniqBy(
						ranked.map(({ document, tier, mergedValues, showCountry }) => {
							const { city, state_code: stateCode, country_code: countryCode } = document;

							const cityStateValue = getCityOrStateLocation(city, stateCode);
							// without a state, "London" is ambiguous, so the label shows the country instead
							const countryName = countryCode ? countryDisplayNameMap[countryCode] : undefined;
							const label =
								(!stateCode || showCountry) && city && countryName ? `${city}, ${countryName}` : cityStateValue;

							return {
								label,
								value: cityStateValue,
								...(mergedValues ? { mergedValues } : {}),
								count: 0,
								// old `?city=London` links select the option "London" was merged into
								isRefined: selectedLocation === cityStateValue || !!mergedValues?.includes(selectedLocation),
								type: 'city' as const,
								tier,
							};
						}),
						'value',
					).slice(0, SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS);
				},
				[countryDisplayNameMap, getCityPetitionCounts, refetchSuggestions, typesenseClient],
			),
		},
	};
}
